import { useCallback, useState } from "react";
import { Flex, Grid, Stack, StackProps } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useSlide } from "../public/slide";
import {
  SlidePropsPair,
  SelectorValue,
  OptionPropsSimple,
  OptionPictureProps,
} from "../public/types";
import { PairState, useQuizActions, useQuizSnapshot } from "./state";
import { MyFormLabel } from "./ui";
import { OptionSimple, OptionWithPicture } from "./option";

function getOptionValue(option: { value?: string; text: string }) {
  return option.value ?? option.text;
}

export type PairSelectProps = {
  /** Props for the root container of the selector */
  containerProps?: StackProps;
  /** Color of the "(Choose one)" label */
  labelColor?: string;
  /** Hides the "(Choose one)" label */
  hideLabel?: boolean;
} & SlidePropsPair;

export function PairSelect(props: PairSelectProps) {
  const actions = useQuizActions();
  const slideCtx = useSlide();
  const snap = useQuizSnapshot();
  const { t } = useTranslation();

  const options = props.options ?? [];

  const selectorState = snap.slideStateByID[slideCtx.id] as PairState | undefined;
  const existingValues = Array.isArray(selectorState?.value) ? selectorState!.value : [];

  const [pairIndex, setPairIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    existingValues[0]?.value ?? null
  );

  const pairStart = pairIndex * 2;
  const currentPair = options.slice(pairStart, pairStart + 2);
  const isLastPair = pairStart + 2 >= options.length;

  const handleClick = useCallback(
    (value: SelectorValue) => {
      setSelectedValue(value.value);
      actions.addPairOption(slideCtx.id, value, pairIndex);
      if (isLastPair) {
        setTimeout(() => {
          const ok = actions.submitQuestion();
          if (ok) {
            actions.goToNext();
          }
        }, 200);
      } else {
        setTimeout(() => {
          const nextIndex = pairIndex + 1;
          setSelectedValue(existingValues[nextIndex]?.value ?? null);
          setPairIndex(nextIndex);
          setAnimKey((k) => k + 1);
        }, 120);
      }
    },
    [isLastPair, pairIndex, existingValues, slideCtx.id, actions]
  );

  return (
    <Stack spacing={1} {...props.containerProps}>
      {!props.hideLabel && (
        <MyFormLabel mx="auto" color={props.labelColor}>
          {props.label ?? t("quizLib.pair.chooseOne", { defaultValue: "(Choose one)" })}
        </MyFormLabel>
      )}
      <AnimatePresence mode="wait">
        <motion.div
          key={animKey}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            opacity: { duration: 0.2, ease: "easeOut" },
            y: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
          }}
        >
          {props.variant === "list" ? (
            props.direction === "grid" ? (
              <Grid width="full" gap={3} gridTemplateColumns="1fr 1fr">
                {(currentPair as OptionPropsSimple[]).map((option, idx) => {
                  const value = getOptionValue(option);
                  const optionValue: SelectorValue = { value, idx: pairStart + idx };
                  return (
                    <OptionSimple
                      key={value}
                      isSelected={selectedValue === value}
                      onClick={() => handleClick(optionValue)}
                      text={option.text}
                      icon={option.icon}
                      selectorIconType="radio"
                      size={props.size}
                      {...option.styleProps}
                    />
                  );
                })}
              </Grid>
            ) : (
              <Flex
                flexDir={props.direction ?? "column"}
                flexWrap={props.direction === "row" ? "wrap" : undefined}
                gap={3}
                justifyContent={props.direction === "row" ? "center" : undefined}
              >
                {(currentPair as OptionPropsSimple[]).map((option, idx) => {
                  const value = getOptionValue(option);
                  const optionValue: SelectorValue = { value, idx: pairStart + idx };
                  return (
                    <OptionSimple
                      key={value}
                      isSelected={selectedValue === value}
                      onClick={() => handleClick(optionValue)}
                      text={option.text}
                      icon={option.icon}
                      selectorIconType="radio"
                      size={props.size}
                      {...option.styleProps}
                    />
                  );
                })}
              </Flex>
            )
          ) : (
            <Flex gap={4} flexWrap="wrap" justifyContent="center">
              {(currentPair as OptionPictureProps[]).map((option, idx) => {
                const value = getOptionValue(option);
                const optionValue: SelectorValue = { value, idx: pairStart + idx };
                return (
                  <OptionWithPicture
                    key={value}
                    isSelected={selectedValue === value}
                    onClick={() => handleClick(optionValue)}
                    text={option.text}
                    imgHeight={props.size === "small" ? 100 : 180}
                    imgComponent={option.imgComponent}
                    hideText={props.hideText}
                    width="45%"
                    {...option.styleProps}
                  />
                );
              })}
            </Flex>
          )}
        </motion.div>
      </AnimatePresence>
    </Stack>
  );
}
